import type { SequenceDirection } from "./plotImageSequence";

/** What the annotation editor did with a review-mode arrow key. */
export type ReviewStepResult = "moved" | "boundary" | "empty" | "inactive";

/** Which end of the next image's object list to land on after navigating. */
export type ReviewLandingEdge = "first" | "last";

export type ReviewArrowOutcome =
  | { kind: "consumed" }
  | { kind: "navigate"; direction: SequenceDirection; landingEdge: ReviewLandingEdge }
  | { kind: "prompt" }
  | { kind: "ignore" };

/**
 * Decide what a left/right arrow does while the annotation editor is open, given what the editor
 * already did with it (`step`) and whether there is another image to move to.
 *
 * Pure so the whole truth table is unit-testable — the arrow handler that consumes it lives in
 * `PlotDetailsSheet`, which has no test harness of its own.
 *
 * The rules, in priority order:
 *  - A cursor that moved swallows the key; nothing else happens.
 *  - With nowhere to navigate we do nothing at all, rather than prompting for a move we can't make.
 *  - Only running off the END of the object list asks first, and only until the reviewer opts out.
 *    Running off the FRONT goes back silently: backward navigation is non-destructive and instantly
 *    re-enterable, so a prompt would be pure friction.
 *  - `landingEdge` tells the next image which end of ITS object list to open on, so sweeping
 *    backward lands on that image's last object. It stays "first" when review mode isn't running,
 *    since a stale "last" must never leak into a later review session.
 *  - Enter is an alias for the right arrow WITHIN review mode only. It never falls through to image
 *    navigation, because a stray Enter changing which image you are on would be a nasty surprise —
 *    but it does reach the end-of-objects prompt, so Enter, Enter, Enter walks the objects and then
 *    confirms the move to the next image.
 */
export function resolveReviewArrowOutcome({
  direction,
  step,
  hasNavigationTarget,
  suppressEndOfObjectsPrompt,
  source = "arrow",
}: {
  direction: SequenceDirection;
  step: ReviewStepResult;
  hasNavigationTarget: boolean;
  suppressEndOfObjectsPrompt: boolean;
  source?: "arrow" | "enter";
}): ReviewArrowOutcome {
  if (step === "moved") return { kind: "consumed" };
  if (source === "enter" && step !== "boundary") return { kind: "ignore" };
  if (!hasNavigationTarget) return { kind: "ignore" };

  if (step === "boundary" && direction === "next" && !suppressEndOfObjectsPrompt) {
    return { kind: "prompt" };
  }

  return {
    kind: "navigate",
    direction,
    landingEdge: step !== "inactive" && direction === "prev" ? "last" : "first",
  };
}
